import { useCallback, useEffect, useRef, useState } from 'react'
import { pickSupportedMimeType, stopStream } from './utils'
import useCaptionRecognition from './useCaptionRecognition'

/**
 * Captures the screen (+ optional microphone, mixed into a single audio
 * track via the Web Audio API) into a single Blob per recording session.
 *
 * Usage:
 *   const rec = useScreenRecorder({ onClipReady: (blob) => addClip(blob) })
 *   rec.start({ withMic: true, withCaptions: true })
 *   rec.pause() / rec.resume()
 *   rec.setMicEnabled(false)   // mute mic without stopping the recording
 *   rec.stop()
 *
 * Robustness notes:
 *  - If the user stops sharing from the browser's own "Stop sharing" bar
 *    (rather than our Stop button), we still finalise and hand back
 *    whatever was captured instead of losing it silently.
 *  - Falls back gracefully if the OS/browser can't supply tab/system audio
 *    (getDisplayMedia audio is best-effort everywhere) or if the mic
 *    permission is denied — recording continues video-only rather than
 *    failing outright.
 *
 * Live captions: `withCaptions` turns on useCaptionRecognition alongside the
 * recording (only takes effect when `withMic` is also on and the browser
 * supports it — see that hook for why). Its segments are handed back in
 * onClipReady's meta as `captions`, timed against the same recording clock
 * so they land in the right place after trims/concatenation later.
 */
const useScreenRecorder = ({ onClipReady, onError } = {}) => {
  const [status, setStatus] = useState('idle') // idle | requesting | recording | paused
  const [elapsedSec, setElapsedSec] = useState(0)
  const [micEnabled, setMicEnabled] = useState(true)
  const [micAvailable, setMicAvailable] = useState(false)

  const captions = useCaptionRecognition()
  const captionsActiveRef = useRef(false) // whether captions were actually requested+started for the current session

  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const displayStreamRef = useRef(null)
  const micStreamRef = useRef(null)
  const audioCtxRef = useRef(null)
  const combinedStreamRef = useRef(null)
  const timerRef = useRef(null)
  const startedAtRef = useRef(0)
  const pausedAccumRef = useRef(0)

  const cleanupStreams = useCallback(() => {
    stopStream(displayStreamRef.current)
    stopStream(micStreamRef.current)
    stopStream(combinedStreamRef.current)
    displayStreamRef.current = null
    micStreamRef.current = null
    combinedStreamRef.current = null
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close() } catch { /* noop */ }
      audioCtxRef.current = null
    }
  }, [])

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const tickTimer = useCallback(() => {
    clearTimer()
    timerRef.current = setInterval(() => {
      setElapsedSec((pausedAccumRef.current + (Date.now() - startedAtRef.current)) / 1000)
    }, 250)
  }, [clearTimer])

  const finalize = useCallback(() => {
    clearTimer()
    const mimeType = recorderRef.current?.mimeType || 'video/webm'
    const blob = new Blob(chunksRef.current, { type: mimeType })
    chunksRef.current = []
    // Read this off the combined stream before cleanupStreams() tears it
    // down — lets the editor know up front whether this clip has an audio
    // track at all (screen-only captures with no system audio and no mic
    // never get one), instead of guessing later when building ffmpeg filters.
    const hasAudio = (combinedStreamRef.current?.getAudioTracks().length || 0) > 0
    let capturedCaptions = []
    if (captionsActiveRef.current) {
      capturedCaptions = captions.getSegments()
      captions.stop()
      captionsActiveRef.current = false
    }
    cleanupStreams()
    setStatus('idle')
    setElapsedSec(0)
    if (blob.size > 0) {
      onClipReady && onClipReady(blob, { hasAudio, captions: capturedCaptions })
    } else {
      onError && onError(new Error('Recording produced no data — please try again.'))
    }
  }, [cleanupStreams, clearTimer, onClipReady, onError, captions.getSegments, captions.stop])

  const start = useCallback(async ({ withMic = true, withCaptions = false } = {}) => {
    try {
      setStatus('requesting')
      chunksRef.current = []

      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30, cursor: 'always' },
        audio: true, // best-effort: tab/system audio, not supported on every OS/browser
      })
      displayStreamRef.current = displayStream

      let micStream = null
      if (withMic && navigator.mediaDevices.getUserMedia) {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true },
          })
          micStreamRef.current = micStream
          setMicAvailable(true)
        } catch {
          // Mic denied/unavailable — continue with whatever audio the screen share provides.
          setMicAvailable(false)
        }
      }

      const videoTrack = displayStream.getVideoTracks()[0]
      // If the user clicks the browser's own "Stop sharing" control, treat it
      // exactly like pressing our Stop button instead of leaving a dead stream.
      if (videoTrack) {
        videoTrack.onended = () => {
          if (recorderRef.current && recorderRef.current.state !== 'inactive') {
            recorderRef.current.stop()
          }
        }
      }

      const screenAudioTracks = displayStream.getAudioTracks()
      let audioTrack = null

      if (screenAudioTracks.length || (micStream && micStream.getAudioTracks().length)) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        const ctx = new AudioCtx()
        audioCtxRef.current = ctx
        const dest = ctx.createMediaStreamDestination()
        if (screenAudioTracks.length) {
          ctx.createMediaStreamSource(new MediaStream(screenAudioTracks)).connect(dest)
        }
        if (micStream && micStream.getAudioTracks().length) {
          ctx.createMediaStreamSource(new MediaStream(micStream.getAudioTracks())).connect(dest)
        }
        audioTrack = dest.stream.getAudioTracks()[0]
      }

      const tracks = [videoTrack, audioTrack].filter(Boolean)
      const combinedStream = new MediaStream(tracks)
      combinedStreamRef.current = combinedStream

      const mimeType = pickSupportedMimeType()
      const recorder = new MediaRecorder(combinedStream, mimeType ? { mimeType } : undefined)
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onstop = finalize
      recorder.onerror = (e) => {
        onError && onError(e.error || new Error('Recording failed'))
        finalize()
      }
      recorderRef.current = recorder

      recorder.start(1000) // gather data every second so a crash/close loses at most ~1s
      startedAtRef.current = Date.now()
      pausedAccumRef.current = 0
      setStatus('recording')
      tickTimer()

      // Captions need a mic to transcribe (see useCaptionRecognition) and
      // browser support — silently skip otherwise rather than erroring the
      // whole recording out over an optional extra.
      const micIsLive = !!(micStreamRef.current && micStreamRef.current.getAudioTracks().length > 0)
      captionsActiveRef.current = withCaptions && withMic && micIsLive && captions.supported
      if (captionsActiveRef.current) captions.start(startedAtRef.current)
    } catch (err) {
      cleanupStreams()
      setStatus('idle')
      onError && onError(err)
    }
  }, [cleanupStreams, finalize, onError, tickTimer, captions.supported, captions.start])

  const pause = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state !== 'recording' || typeof recorder.pause !== 'function') return
    recorder.pause()
    pausedAccumRef.current += Date.now() - startedAtRef.current
    clearTimer()
    setStatus('paused')
    if (captionsActiveRef.current) captions.pause()
  }, [clearTimer, captions.pause])

  const resume = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state !== 'paused') return
    recorder.resume()
    startedAtRef.current = Date.now()
    setStatus('recording')
    tickTimer()
    if (captionsActiveRef.current) captions.resume()
  }, [tickTimer, captions.resume])

  const stop = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') return
    recorder.stop()
  }, [])

  const setMicEnabledFn = useCallback((enabled) => {
    setMicEnabled(enabled)
    const track = micStreamRef.current?.getAudioTracks()[0]
    if (track) track.enabled = enabled
  }, [])

  // Safety net: release camera/mic/share indicator if the component unmounts mid-recording.
  useEffect(() => () => {
    clearTimer()
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch { /* noop */ }
    }
    cleanupStreams()
    if (captionsActiveRef.current) captions.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanupStreams, clearTimer])

  return {
    status, // idle | requesting | recording | paused
    elapsedSec,
    micEnabled,
    micAvailable,
    supportsPause: typeof MediaRecorder !== 'undefined' && !!MediaRecorder.prototype.pause,
    captionsSupported: captions.supported,
    start,
    pause,
    resume,
    stop,
    setMicEnabled: setMicEnabledFn,
  }
}

export default useScreenRecorder
